<template>
  <div class="language-switcher">
    <div class="relative">
      <button
        @click="toggleDropdown"
        class="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-700/30 transition-colors text-sm"
      >
        <div class="i-lucide-languages"></div>
        <span>{{ t('common.language') }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      <div 
        v-if="isOpen" 
        class="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg z-50"
      >
        <div class="py-1">
          <button
            v-for="locale in availableLocales"
            :key="locale.code"
            @click="changeLanguage(locale.code)"
            class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-700 transition-colors"
            :class="{ 'bg-blue-600/30': locale.code === currentLocale }"
          >
            {{ locale.name }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from '#imports';
import { useI18n } from 'vue-i18n';
import { useLocale } from '~/composables/useLocale';

const { t } = useI18n();
const { getCurrentLocale, setLocale, availableLocales } = useLocale();
const isOpen = ref(false);

// 获取当前语言代码
const currentLocale = computed(() => getCurrentLocale());

// 获取当前语言名称
const currentLanguageName = computed(() => {
  const current = availableLocales.find(l => l.code === currentLocale.value);
  return current ? current.name : 'Language';
});

// 切换语言
const changeLanguage = (code: string) => {
  if (setLocale(code)) {
    isOpen.value = false;
  }
};

// 切换下拉菜单
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

// 点击外部关闭下拉菜单
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest('.language-switcher')) {
    isOpen.value = false;
  }
};

// 监听点击事件
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  
  // 监听语言变化事件
  window.addEventListener('language-changed', () => {
    console.log('LanguageSwitcher - 检测到语言变化事件');
  });
});

// 移除事件监听
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('language-changed', () => {});
});
</script>

<style scoped>
.language-switcher {
  position: relative;
}

/* 添加下拉菜单的过渡动画 */
.language-switcher .absolute {
  transition: all 0.2s ease-in-out;
  opacity: 1;
  visibility: visible;
}

/* 确保下拉菜单在hover时不会消失 */
.language-switcher:hover .absolute {
  opacity: 1;
  visibility: visible;
}

/* 添加一个小的延迟，防止鼠标移动到下拉菜单时立即消失 */
.language-switcher .absolute {
  pointer-events: auto;
  transition-delay: 0.5s;
}

/* 确保下拉菜单容器不会影响hover状态 */
.language-switcher > div {
  pointer-events: auto;
}
</style> 
